import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Bell, 
  AlertCircle, 
  Clock, 
  Calendar, 
  Flag, 
  CheckCheck, 
  CheckCircle2, 
  ChevronRight,
  Trash2
} from 'lucide-react';
import { AppNotification, NotificationType } from '../utils/notificationUtils';

interface NotificationCenterProps {
  notifications: AppNotification[];
  unreadCount: number;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onNavigateToTask: (projectId: string, taskId: string | number) => void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onNavigateToTask
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const displayedNotifications = useMemo(() => {
    if (filter === 'unread') {
      return notifications.filter(n => !n.read);
    }
    return notifications;
  }, [notifications, filter]);

  const getIconForType = (type: NotificationType) => {
    switch (type) {
      case 'overdue':
        return <AlertCircle size={15} className="text-rose-500 shrink-0" />;
      case 'due_today':
        return <Clock size={15} className="text-amber-500 shrink-0" />;
      case 'due_soon':
        return <Calendar size={15} className="text-indigo-400 shrink-0" />;
      case 'milestone':
        return <Flag size={15} className="text-amber-400 shrink-0" />;
      default:
        return <Bell size={15} className="text-slate-400 shrink-0" />;
    }
  };

  const getBgForType = (type: NotificationType, read: boolean) => {
    if (read) return 'hover:bg-white/5 opacity-70';
    switch (type) {
      case 'overdue':
        return 'bg-rose-500/10 hover:bg-rose-500/15 border-rose-500/20';
      case 'due_today':
        return 'bg-amber-500/10 hover:bg-amber-500/15 border-amber-500/20';
      case 'due_soon':
        return 'bg-indigo-500/10 hover:bg-indigo-500/15 border-indigo-500/20';
      case 'milestone':
        return 'bg-purple-500/10 hover:bg-purple-500/15 border-purple-500/20';
      default:
        return 'bg-white/5 hover:bg-white/10 border-white/5';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-xl border transition-all active:scale-95 flex items-center justify-center ${
          isOpen
            ? 'bg-white/10 border-indigo-500/50 text-white'
            : 'bg-white/5 border-white/10 text-slate-300 hover:text-white hover:bg-white/10'
        }`}
        title="Benachrichtigungszentrale"
        aria-label="Benachrichtigungen"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] px-1 items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white shadow-lg shadow-rose-500/50 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Flyout Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-slate-900/95 border border-white/15 rounded-2xl shadow-2xl z-[120] backdrop-blur-2xl flex flex-col overflow-hidden text-left"
          >
            {/* Header */}
            <div className="p-3.5 border-b border-white/10 flex items-center justify-between bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <span className="text-xs font-black uppercase tracking-wider text-white">Benachrichtigungen</span>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/30 text-rose-400 text-[10px] font-black">
                    {unreadCount} neu
                  </span>
                )}
              </div>

              {unreadCount > 0 && (
                <button
                  onClick={onMarkAllAsRead}
                  className="flex items-center gap-1 text-[11px] font-bold text-indigo-400 hover:text-indigo-300 transition-colors p-1 rounded-lg hover:bg-white/5"
                  title="Alle als gelesen markieren"
                >
                  <CheckCheck size={13} />
                  <span>Alle gelesen</span>
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="flex border-b border-white/5 px-2 pt-1 gap-1 text-xs">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 font-bold rounded-lg transition-all ${
                  filter === 'all'
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                Alle ({notifications.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1.5 font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                  filter === 'unread'
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <span>Ungelesen</span>
                {unreadCount > 0 && (
                  <span className="w-2 h-2 rounded-full bg-rose-500" />
                )}
              </button>
            </div>

            {/* Notification List */}
            <div className="max-h-80 overflow-y-auto divide-y divide-white/5 p-1.5 space-y-1">
              {displayedNotifications.length === 0 ? (
                <div className="p-8 text-center flex flex-col items-center justify-center gap-2 text-slate-400">
                  <div className="w-10 h-10 rounded-2xl bg-white/5 flex items-center justify-center text-slate-500">
                    <CheckCircle2 size={20} className="text-emerald-400" />
                  </div>
                  <p className="text-xs font-bold text-slate-300">Alles erledigt!</p>
                  <p className="text-[10px] text-slate-500">Keine ausstehenden Benachrichtigungen vorhanden.</p>
                </div>
              ) : (
                displayedNotifications.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => {
                      onMarkAsRead(n.id);
                      setIsOpen(false);
                      onNavigateToTask(n.projectId, n.taskId);
                    }}
                    className={`p-2.5 rounded-xl border transition-all cursor-pointer flex items-start gap-2.5 group ${getBgForType(
                      n.type,
                      n.read
                    )}`}
                  >
                    <div className="pt-0.5">{getIconForType(n.type)}</div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className="text-[9px] font-black uppercase tracking-wider text-slate-400 truncate flex items-center gap-1">
                          <span
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${n.projectColor || 'bg-indigo-500'}`}
                          />
                          {n.projectName}
                        </span>
                        {!n.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                        )}
                      </div>

                      <p className="text-xs font-bold text-white group-hover:text-indigo-300 transition-colors truncate">
                        {n.taskName}
                      </p>

                      <p className="text-[10px] text-slate-400 mt-0.5 line-clamp-1">
                        {n.message}
                      </p>
                    </div>

                    <div className="pt-1 text-slate-500 group-hover:text-white transition-colors">
                      <ChevronRight size={13} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
